import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  message,
  Modal,
  Avatar,
  Tooltip,
  Form,
  Select,
  Spin,
  Empty,
  Popover,
} from 'antd';
import {
  PhoneOutlined,
  ReloadOutlined,
  SyncOutlined,
  EyeOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  RobotOutlined,
  FileDoneOutlined,
  AudioOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { callCenterCallsApi, callCenterAgentsApi, callCenterOutcomesApi } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import PersianDatePicker from '../../components/PersianDatePicker';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import { toJalali } from '../../utils/dateUtils';
import { parseJalaliDateRangeValue, formatJalaliDateForApi } from '../../utils/filterDateUtils';
import { useListReturnState } from '../../hooks/useListReturnState';
import { useCallProcessingStatus } from '../../hooks/useCallProcessingStatus';
import CallAiSummaryPanel, {
  AiAnalysisTags,
  canSummarizeCall,
  hasAnalysisReport,
  isAiInProgress,
} from '../../components/call-center/CallAiSummaryPanel';
import CallTranscriptView from '../../components/call-center/CallTranscriptView';
import AiBulkSummaryCard from '../../components/call-center/AiBulkSummaryCard';
import CallReportModal from '../../components/call-center/CallReportModal';
import CallSubjectChip from '../../components/call-center/subjects/CallSubjectChip';
import CallSubjectSelect from '../../components/call-center/subjects/CallSubjectSelect';
import { useCallSubjects } from '../../components/call-center/subjects/useCallSubjects';
import { useCallObjections } from '../../components/call-center/objections/useCallObjections';

const DIRECTION_META = {
  incoming: { label: 'ورودی', color: 'blue', icon: <ArrowDownOutlined /> },
  outgoing: { label: 'خروجی', color: 'green', icon: <ArrowUpOutlined /> },
  missed: { label: 'از دست رفته', color: 'red', icon: <PhoneOutlined /> },
};

const DIRECTION_FILTER_OPTIONS = [
  { value: 'all', label: 'همه' },
  { value: 'incoming', label: 'ورودی' },
  { value: 'outgoing', label: 'خروجی' },
  { value: 'missed', label: 'از دست رفته' },
];

const DISPOSITION_META = {
  ANSWERED: { label: 'پاسخ داده', color: 'green' },
  'NO ANSWER': { label: 'بی‌پاسخ', color: 'orange' },
  NOANSWER: { label: 'بی‌پاسخ', color: 'orange' },
  BUSY: { label: 'مشغول', color: 'gold' },
  FAILED: { label: 'ناموفق', color: 'red' },
  failed: { label: 'خطا', color: 'error' },
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Number(seconds) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const toLatinDigits = (value) => {
  if (value == null) return '';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = String(value);
  for (let i = 0; i < 10; i += 1) {
    result = result.replace(new RegExp(persianDigits[i], 'g'), String(i));
    result = result.replace(new RegExp(arabicDigits[i], 'g'), String(i));
  }
  return result;
};

const formatDatePickerValue = (dateObj) => {
  if (!dateObj) return null;
  if (typeof dateObj === 'string') return toLatinDigits(dateObj).replace(/\//g, '-');
  if (dateObj?.format) return toLatinDigits(dateObj.format('YYYY/MM/DD')).replace(/\//g, '-');
  return null;
};

/** فیلتر ریسک از لینک داشبورد: /call-center/calls?risk=needs_review */
const readRiskFromUrl = () => {
  try {
    const risk = new URLSearchParams(window.location.search).get('risk');
    return ['high_risk', 'needs_review', 'missed_opportunity', 'missed_review'].includes(risk) ? risk : undefined;
  } catch {
    return undefined;
  }
};

const buildDefaultCallFilters = () => ({
  direction: 'all',
  startDate: null,
  endDate: null,
  extension: undefined,
  agent_user_id: undefined,
  subject_id: undefined,
  objection_id: undefined,
  risk: undefined,
  phone: '',
});

const CallCenterCalls = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncForm] = Form.useForm();
  const [loadingExtensions, setLoadingExtensions] = useState(false);
  const [extensionOptions, setExtensionOptions] = useState([]);
  const [calls, setCalls] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [filters, setFilters] = useState(() => ({ ...buildDefaultCallFilters(), risk: readRiskFromUrl() }));
  const [agentOptions, setAgentOptions] = useState([]);
  const [filterExtOptions, setFilterExtOptions] = useState([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [textModalLoading, setTextModalLoading] = useState(false);
  const [textModalCall, setTextModalCall] = useState(null);
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [audioModalLoading, setAudioModalLoading] = useState(false);
  const [audioLoadingId, setAudioLoadingId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioCallLabel, setAudioCallLabel] = useState('');
  const [transcribingIds, setTranscribingIds] = useState([]);
  const [loadingOutcomeId, setLoadingOutcomeId] = useState(null);
  const { subjects } = useCallSubjects();
  const { objections } = useCallObjections();

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current_page: pagination.current_page,
        per_page: pagination.per_page,
      },
    }),
    [filters, pagination.current_page, pagination.per_page],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      const restoredPage = Number(restored.pagination.current_page) || 1;
      const restoredSize = Number(restored.pagination.per_page) || pagination.per_page;
      setPagination((prev) => ({
        ...prev,
        current_page: restoredPage,
        per_page: restoredSize,
      }));
      fetchCalls(restoredPage, restoredSize, restored.filters);
    }
  });

  const loadFilterOptions = useCallback(async () => {
    if (agentsLoaded) return;
    try {
      const res = await callCenterAgentsApi.getAll();
      const agents = res?.data ?? [];
      setAgentOptions(
        agents
          .filter((a) => a.user)
          .map((a) => ({
            value: a.user_id,
            label: a.user.name,
          })),
      );
      setFilterExtOptions(
        agents
          .filter((a) => a.extension)
          .map((a) => ({
            value: String(a.extension),
            label: `داخلی ${a.extension}${a.user?.name ? ` — ${a.user.name}` : ''}`,
          })),
      );
      setAgentsLoaded(true);
      
      const outRes = await callCenterOutcomesApi.getAll();
      const payload = outRes?.data ?? [];
      setOutcomes(Array.isArray(payload) ? payload : payload.data ?? []);
    } catch {
      // silent
    }
  }, [agentsLoaded]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const buildQueryParams = useCallback((f) => {
    const q = {};
    if (f.direction && f.direction !== 'all') q.direction = f.direction;
    else if (f.direction === 'all') q.direction = 'all';
    if (f.startDate) {
      const from = formatJalaliDateForApi(f.startDate);
      if (from) q.from = from.replace(/-/g, '/');
    }
    if (f.endDate) {
      const to = formatJalaliDateForApi(f.endDate);
      if (to) q.to = to.replace(/-/g, '/');
    }
    if (f.extension) q.extension = f.extension;
    const phone = toLatinDigits(f.phone).trim();
    if (phone) q.phone = phone;
    if (f.agent_user_id) q.agent_user_id = f.agent_user_id;
    if (f.subject_id) q.subject_id = f.subject_id;
    if (f.objection_id) q.objection_id = f.objection_id;
    if (f.risk === 'high_risk') q.high_risk = 1;
    if (f.risk === 'needs_review') q.needs_review = 1;
    if (f.risk === 'missed_opportunity') q.missed_opportunity = 1;
    if (f.risk === 'missed_review') q.missed_review = 1;
    return q;
  }, []);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchCalls = useCallback(async (page = 1, perPage = 20, activeFilters) => {
    const f = activeFilters ?? filtersRef.current;
    const targetPage = Number(page) || 1;
    const targetPerPage = Number(perPage) || 20;
    setLoading(true);
    try {
      const q = buildQueryParams(f);
      const res = await callCenterCallsApi.getAll({ page: targetPage, per_page: targetPerPage, ...q });
      const raw = res?.data ?? res ?? {};

      let callList = [];
      let totalCount = 0;
      let currentPage = targetPage;
      let perPageCount = targetPerPage;

      if (Array.isArray(raw)) {
        callList = raw;
        totalCount = raw.length;
      } else if (raw && Array.isArray(raw.data)) {
        callList = raw.data;
        totalCount = Number(raw.total ?? raw.data.length);
        currentPage = Number(raw.current_page ?? targetPage);
        perPageCount = Number(raw.per_page ?? targetPerPage);
      } else if (raw && Array.isArray(raw.items)) {
        callList = raw.items;
        totalCount = Number(raw.total ?? raw.items.length);
        currentPage = Number(raw.page ?? targetPage);
        perPageCount = Number(raw.per_page ?? targetPerPage);
      }

      setCalls(callList);
      setPagination({
        current_page: currentPage,
        per_page: perPageCount,
        total: totalCount,
        last_page: Math.max(1, Math.ceil(totalCount / perPageCount)),
      });
    } catch (error) {
      console.error('خطا در دریافت تماس‌ها:', error);
      message.error(error?.message || 'خطا در دریافت لیست تماس‌ها');
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchCalls(pagination.current_page, pagination.per_page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phoneDebounceRef = useRef(null);
  useEffect(() => () => clearTimeout(phoneDebounceRef.current), []);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'phone') {
      const next = { ...filtersRef.current, phone: value ?? '' };
      filtersRef.current = next;
      setFilters(next);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      clearTimeout(phoneDebounceRef.current);
      phoneDebounceRef.current = setTimeout(() => fetchCalls(1, pagination.per_page, next), 400);
      return;
    }
    if (key === 'dateRange') {
      const { startDate, endDate } = parseJalaliDateRangeValue(value);
      setFilters((prev) => {
        const next = { ...prev, startDate, endDate };
        setTimeout(() => fetchCalls(1, pagination.per_page, next), 0);
        return next;
      });
    } else {
      setFilters((prev) => {
        const next = { ...prev, [key]: value ?? undefined };
        setTimeout(() => fetchCalls(1, pagination.per_page, next), 0);
        return next;
      });
    }
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  }, [fetchCalls, pagination.per_page]);

  const handleResetFilters = useCallback(() => {
    clearTimeout(phoneDebounceRef.current);
    const def = buildDefaultCallFilters();
    setFilters(def);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    fetchCalls(1, pagination.per_page, def);
  }, [fetchCalls, pagination.per_page]);

  const handlePageChange = useCallback((page, pageSize) => {
    const targetPage = Number(page) || 1;
    const targetSize = Number(pageSize) || pagination.per_page || 20;
    setPagination((prev) => ({ ...prev, current_page: targetPage, per_page: targetSize }));
    fetchCalls(targetPage, targetSize);
  }, [fetchCalls, pagination.per_page]);

  const quickFilters = useMemo(() => [
    {
      key: 'direction',
      type: 'select',
      label: 'نوع تماس',
      placeholder: 'نوع تماس',
      value: filters.direction,
      options: DIRECTION_FILTER_OPTIONS,
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'بازه تاریخ',
      placeholder: 'از تاریخ — تا تاریخ',
      value: filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : undefined,
    },
    {
      key: 'phone',
      type: 'input',
      label: 'شماره تلفن',
      placeholder: 'جستجوی شماره تلفن',
      value: filters.phone,
      inputMode: 'tel',
      maxLength: 30,
    },
    {
      key: 'extension',
      type: 'select',
      label: 'داخلی',
      placeholder: 'همه داخلی‌ها',
      value: filters.extension,
      options: filterExtOptions,
      showSearch: true,
    },
    {
      key: 'agent_user_id',
      type: 'select',
      label: 'اپراتور',
      placeholder: 'همه اپراتورها',
      value: filters.agent_user_id,
      options: agentOptions,
      showSearch: true,
    },
    {
      key: 'subject_id',
      type: 'select',
      label: 'موضوع',
      placeholder: 'همه موضوعات',
      value: filters.subject_id,
      options: subjects.map((s) => ({ value: s.id, label: s.name })),
      showSearch: true,
    },
    {
      key: 'objection_id',
      type: 'select',
      label: 'اعتراض',
      placeholder: 'همه اعتراض‌ها',
      value: filters.objection_id,
      options: objections.map((o) => ({ value: o.id, label: o.name })),
      showSearch: true,
    },
    {
      key: 'risk',
      type: 'select',
      label: 'ریسک و فرصت فروش',
      placeholder: 'همه تماس‌ها',
      value: filters.risk,
      options: [
        { value: 'needs_review', label: 'پرریسک — نیازمند بررسی' },
        { value: 'high_risk', label: 'همه تماس‌های پرریسک' },
        { value: 'missed_review', label: 'فرصت فروش ازدست‌رفته — نیازمند پیگیری' },
        { value: 'missed_opportunity', label: 'همه فرصت‌های فروش ازدست‌رفته' },
      ],
    },
  ], [filters, filterExtOptions, agentOptions, subjects, objections]);

  const openSyncModal = async () => {
    setSyncModalOpen(true);
    syncForm.resetFields();
    setLoadingExtensions(true);
    try {
      const res = await callCenterAgentsApi.getAll();
      const agents = res?.data ?? [];
      const exts = agents
        .filter((a) => a.extension)
        .map((a) => ({
          value: String(a.extension),
          label: `داخلی ${a.extension}${a.user?.name ? ` — ${a.user.name}` : ''}`,
        }));
      setExtensionOptions(exts);
      syncForm.setFieldsValue({
        extensions: exts.map((e) => e.value),
      });
    } catch (error) {
      console.error('خطا در دریافت داخلی‌ها:', error);
      message.error('خطا در دریافت لیست داخلی‌ها');
    } finally {
      setLoadingExtensions(false);
    }
  };

  const submitSync = async () => {
    try {
      const values = await syncForm.validateFields();
      const from = formatDatePickerValue(values.from_date);
      const to = formatDatePickerValue(values.to_date);
      if (!from || !to) {
        message.error('لطفاً تاریخ شروع و پایان را انتخاب کنید');
        return;
      }
      setSyncing(true);
      const res = await callCenterCallsApi.sync({
        sync: true,
        from,
        to,
        extensions: values.extensions ?? [],
      });
      const data = res?.data ?? {};
      if (data.queued) {
        message.success(res?.message || 'همگام‌سازی در صف قرار گرفت');
      } else {
        message.success(
          res?.message ||
          `همگام‌سازی انجام شد — ${data.imported ?? 0} تماس وارد شد`,
        );
      }
      setSyncModalOpen(false);
      await fetchCalls(1, pagination.per_page);
      setPagination((prev) => ({ ...prev, current_page: 1 }));
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در همگام‌سازی:', error);
      message.error(error?.message || 'خطا در همگام‌سازی تماس‌ها');
    } finally {
      setSyncing(false);
    }
  };

  const openTextModal = async (row) => {
    setTextModalOpen(true);
    setTextModalCall(null);
    setTextModalLoading(true);
    try {
      const res = await callCenterCallsApi.getById(row.id);
      setTextModalCall(res?.data ?? null);
    } catch (error) {
      console.error('خطا در دریافت متن تماس:', error);
      message.error(error?.message || 'خطا در دریافت متن تماس');
      setTextModalOpen(false);
    } finally {
      setTextModalLoading(false);
    }
  };

  const playCallAudio = async (row) => {
    if (!row?.audio_file && !row?.audio_url) {
      message.warning('فایل صوتی برای این تماس در دسترس نیست');
      return;
    }
    setAudioLoadingId(row.id);
    setAudioModalLoading(true);
    setAudioUrl(null);
    setAudioCallLabel(row.phone || `#${row.id}`);
    setAudioModalOpen(true);
    try {
      const res = await callCenterCallsApi.getAudioLink(row.id);
      const link = res?.data?.audio_link;
      if (!link) {
        message.warning('لینک فایل صوتی در دسترس نیست');
        setAudioModalOpen(false);
        return;
      }
      setAudioUrl(link);
    } catch (error) {
      console.error('خطا در دریافت فایل صوتی:', error);
      message.error(error?.message || 'خطا در دریافت فایل صوتی');
      setAudioModalOpen(false);
    } finally {
      setAudioLoadingId(null);
      setAudioModalLoading(false);
    }
  };

  const handleTranscribe = useCallback(async (callId) => {
    setTranscribingIds((prev) => [...prev, callId]);
    try {
      const res = await callCenterCallsApi.transcribe(callId);
      message.success(res?.message || 'درخواست تبدیل صوت به متن در صف پردازش قرار گرفت.');
      setCalls((prev) => prev.map(c => c.id === callId ? { ...c, transcribe_status: 'pending' } : c));
    } catch (error) {
      console.error('Transcription error:', error);
      message.error(error?.message || 'خطا در درخواست رونویسی');
    } finally {
      setTranscribingIds((prev) => prev.filter((id) => id !== callId));
    }
  }, []);

  const [savingSubjectId, setSavingSubjectId] = useState(null);

  // تغییر خوش‌بینانه موضوع: چیپ فوراً عوض می‌شود و در صورت خطا به حالت قبل برمی‌گردد
  const handleUpdateSubject = useCallback(async (call, subjectId, subject) => {
    const previous = { subject_id: call.subject_id ?? null, subject: call.subject ?? null };
    const next = { subject_id: subjectId, subject: subject ? { id: subject.id, name: subject.name, color: subject.color } : null };
    setCalls((prev) => prev.map((c) => (c.id === call.id ? { ...c, ...next } : c)));
    setSavingSubjectId(call.id);
    try {
      await callCenterCallsApi.update(call.id, { subject_id: subjectId });
    } catch (error) {
      setCalls((prev) => prev.map((c) => (c.id === call.id ? { ...c, ...previous } : c)));
      message.error(error?.message || 'خطا در ثبت موضوع تماس');
    } finally {
      setSavingSubjectId(null);
    }
  }, []);

  const handleUpdateOutcome = useCallback(async (callId, outcomeId) => {
    setLoadingOutcomeId(callId);
    try {
      await callCenterCallsApi.update(callId, { outcome_id: outcomeId });
      message.success('نتیجه تماس با موفقیت ثبت شد');
      setCalls((prev) =>
        prev.map((c) => (c.id === callId ? { ...c, outcome_id: outcomeId, outcome: outcomes.find(o => o.id === outcomeId) } : c))
      );
    } catch (error) {
      console.error('خطا در ثبت نتیجه:', error);
      message.error(error?.message || 'خطا در ثبت نتیجه');
    } finally {
      setLoadingOutcomeId(null);
    }
  }, [outcomes]);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [editingCallForReport, setEditingCallForReport] = useState(null);

  const handleOpenReportModal = useCallback((row) => {
    setEditingCallForReport(row);
    setReportModalOpen(true);
  }, []);

  const handleCloseReportModal = useCallback(() => setReportModalOpen(false), []);

  const handleReportSaved = useCallback((callId, report) => {
    setCalls((prev) => prev.map((c) => (c.id === callId ? { ...c, report } : c)));
  }, []);

  const [summarizingIds, setSummarizingIds] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkSummarizing, setBulkSummarizing] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const patchCall = useCallback((callId, patch) => {
    setCalls((prev) => prev.map((c) => (c.id === callId ? { ...c, ...patch } : c)));
    setTextModalCall((prev) => (prev && prev.id === callId ? { ...prev, ...patch } : prev));
  }, []);

  const handleSummarizeCall = useCallback(async (callId) => {
    setSummarizingIds((prev) => [...prev, callId]);
    try {
      const res = await callCenterCallsApi.summarize(callId);
      message.success(res?.message || 'درخواست خلاصه‌سازی در صف پردازش قرار گرفت.');
      patchCall(callId, { ai_status: 'pending', ai_error: null });
    } catch (error) {
      console.error('AI summary error:', error);
      message.error(error?.message || 'خطا در درخواست خلاصه‌سازی');
    } finally {
      setSummarizingIds((prev) => prev.filter((id) => id !== callId));
    }
  }, [patchCall]);

  const handleSummarizeSelected = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkSummarizing(true);
    try {
      const res = await callCenterCallsApi.summarizeBulk({ call_ids: selectedRowKeys, mode: 'all' });
      message.success(res?.message || 'تماس‌های انتخاب‌شده در صف خلاصه‌سازی قرار گرفتند.');
      const queued = new Set(selectedRowKeys);
      setCalls((prev) => prev.map((c) => (queued.has(c.id) && canSummarizeCall(c) && !isAiInProgress(c.ai_status)
        ? { ...c, ai_status: 'pending', ai_error: null }
        : c)));
      setSelectedRowKeys([]);
    } catch (error) {
      message.error(error?.message || 'خطا در خلاصه‌سازی گروهی');
    } finally {
      setBulkSummarizing(false);
    }
  }, [selectedRowKeys]);

  const bulkBaseFilters = useMemo(() => buildQueryParams(filters), [buildQueryParams, filters]);

  // فقط تماس‌های در حال پردازش (رونویسی یا خلاصه) با یک درخواست سبک polling می‌شوند
  const processingIds = useMemo(
    () => calls
      .filter((c) => c.transcribe_status === 'pending' || c.transcribe_status === 'processing' || isAiInProgress(c.ai_status))
      .map((c) => c.id),
    [calls],
  );

  useCallProcessingStatus(processingIds, (updates) => {
    const byId = new Map(updates.map((u) => [u.id, u]));
    setCalls((prev) => prev.map((c) => (byId.has(c.id) ? { ...c, ...byId.get(c.id) } : c)));
    setTextModalCall((prev) => {
      if (!prev || !byId.has(prev.id)) return prev;
      const update = byId.get(prev.id);
      // پایان رونویسی: متن کامل سگمنت‌ها فقط در جزئیات تماس موجود است
      if (update.transcribe_status === 'completed' && prev.transcribe_status !== 'completed') {
        callCenterCallsApi.getById(prev.id)
          .then((res) => setTextModalCall((cur) => (cur && cur.id === prev.id ? res?.data ?? cur : cur)))
          .catch(() => {});
      }
      return { ...prev, ...update };
    });
  });

  const columns = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        render: (id) => <span className="custom-font" dir="ltr">{id}</span>,
      },
      {
        title: 'نوع تماس',
        dataIndex: 'direction',
        key: 'direction',
        render: (dir) => {
          const meta = DIRECTION_META[dir] || { label: dir || '—', color: 'default' };
          return (
            <Tag color={meta.color} icon={meta.icon} className="custom-font">
              {meta.label}
            </Tag>
          );
        },
      },
      {
        title: 'شماره / مخاطب',
        key: 'contact',
        render: (_, row) => {
          const primary = row.match?.primary;
          const client = row.client;
          const lead = row.lead;
          const deal = row.deal;
          const contactName = primary?.name || client?.name || lead?.client_name || deal?.name;
          const contactType = primary?.type || (client ? 'client' : lead ? 'lead' : deal ? 'deal' : null);
          const phone = row.phone || row.src || '—';

          if (contactName) {
            const typeLabel = contactType === 'client' ? 'مشتری' : contactType === 'lead' ? 'سرنخ' : contactType === 'deal' ? 'معامله' : null;
            const typeColor = contactType === 'client' ? 'green' : contactType === 'lead' ? 'orange' : contactType === 'deal' ? 'blue' : 'default';

            return (
              <div>
                <div className="custom-font font-medium flex items-center gap-1.5 flex-wrap">
                  <span>{contactName}</span>
                  {typeLabel && (
                    <Tag color={typeColor} className="custom-font m-0 text-xs px-1.5 py-0 leading-normal">
                      {typeLabel}
                    </Tag>
                  )}
                </div>
                <div className="custom-font text-xs text-gray-500" dir="ltr">
                  {phone}
                </div>
              </div>
            );
          }
          return <span className="custom-font" dir="ltr">{phone}</span>;
        },
      },
      {
        title: 'اپراتور',
        key: 'agent',
        render: (_, row) => {
          const agent = row.agent;
          if (!agent?.name) return <span className="text-gray-400 custom-font">—</span>;
          return (
            <Space>
              <Avatar src={getImagePath(agent.image)} size={32}>
                {agent.name.charAt(0)}
              </Avatar>
              <span className="custom-font">{agent.name}</span>
            </Space>
          );
        },
      },
      {
        title: 'داخلی',
        dataIndex: 'extension',
        key: 'extension',
        render: (ext) => <span className="custom-font" dir="ltr">{ext || '—'}</span>,
      },
      {
        title: 'وضعیت',
        dataIndex: 'disposition',
        key: 'disposition',
        render: (disp) => {
          const meta = DISPOSITION_META[disp] || { label: disp || '—', color: 'default' };
          return <Tag color={meta.color} className="custom-font">{meta.label}</Tag>;
        },
      },
      {
        title: 'مدت مکالمه',
        dataIndex: 'duration',
        key: 'duration',
        render: (sec) => (
          <span className="custom-font" dir="ltr">{formatDuration(sec)}</span>
        ),
      },
      {
        title: 'زمان',
        dataIndex: 'started_at',
        key: 'started_at',
        render: (dt) => {
          if (!dt) return <span className="text-gray-400 custom-font">—</span>;
          const jalali = toJalali(dt, 'jYYYY/jMM/jDD HH:mm');
          return (
            <span className="custom-font text-sm">{jalali || dt}</span>
          );
        },
      },
      {
        title: 'موضوع',
        key: 'subject_id',
        width: 170,
        render: (_, row) => (
          <CallSubjectSelect
            borderless
            value={row.subject_id}
            loading={savingSubjectId === row.id}
            onChange={(subjectId, subject) => handleUpdateSubject(row, subjectId, subject)}
            placeholder="بدون موضوع"
          />
        ),
      },
      {
        title: 'اعتراض‌ها',
        key: 'objections',
        width: 190,
        render: (_, row) => ((row.objections || []).length === 0
          ? <span className="text-gray-300">—</span>
          : (
            <div className="flex flex-wrap gap-1">
              {row.objections.map((o) => <CallSubjectChip key={o.id} subject={o} size="small" />)}
            </div>
          )),
      },
      {
        title: 'ارزیابی',
        key: 'evaluation',
        width: 190,
        render: (_, row) => {
          const hasAny = row.high_risk || row.missed_opportunity || row.operator_score != null || row.fcr != null;
          if (!hasAny) return <span className="text-gray-300">—</span>;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {row.high_risk && (
                <Tooltip title={(row.high_risk_reasons || []).map((r) => r.label).join('، ') || 'تماس پرریسک'}>
                  <Tag color={row.risk_reviewed_at ? 'orange' : 'red'} className="custom-font m-0">
                    🚨 {row.risk_reviewed_at ? 'بررسی‌شده' : 'پرریسک'}
                  </Tag>
                </Tooltip>
              )}
              {row.missed_opportunity && (
                <Tooltip
                  title={(row.missed_opportunity_details?.signals || [])
                    .map((s) => `${s.signal_label}: ${s.gap_label}`)
                    .join('، ') || row.missed_opportunity_details?.summary || 'فرصت فروش ازدست‌رفته'}
                >
                  <Tag color={row.missed_opportunity_reviewed_at ? 'default' : 'volcano'} className="custom-font m-0">
                    💸 {row.missed_opportunity_reviewed_at ? 'فرصت فروش (پیگیری‌شده)' : 'فرصت فروش ازدست‌رفته'}
                  </Tag>
                </Tooltip>
              )}
              {row.operator_score != null && (
                <Tooltip title="امتیاز اپراتور از ۱۰۰">
                  <Tag className="custom-font m-0" color={row.operator_score >= 80 ? 'green' : row.operator_score >= 60 ? 'gold' : 'red'}>★ {row.operator_score}</Tag>
                </Tooltip>
              )}
              {row.fcr != null && (
                <Tooltip title="حل در تماس اول (FCR)">
                  <Tag className="custom-font m-0" color={row.fcr ? 'green' : 'default'}>FCR {row.fcr ? '✓' : '✗'}</Tag>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: 'نتیجه تماس',
        key: 'outcome_id',
        width: 180,
        render: (_, row) => {
          const availableOutcomes = outcomes.filter(o => !o.queue_id || !row.queue_id || o.queue_id === row.queue_id);
          if (availableOutcomes.length === 0) return <span className="text-gray-400 text-sm custom-font">—</span>;
          
          return (
            <Select
              className="w-full custom-font text-sm"
              style={{ minWidth: 140 }}
              placeholder="بدون نتیجه (کلیک کنید)"
              size="middle"
              value={row.outcome_id || undefined}
              onChange={(val) => handleUpdateOutcome(row.id, val)}
              loading={loadingOutcomeId === row.id}
              allowClear
              popupClassName="custom-font text-sm"
              options={availableOutcomes.map(o => ({ value: o.id, label: o.name }))}
            />
          );
        },
      },
      {
        title: 'گزارش / یادداشت',
        key: 'report',
        width: 180,
        render: (_, row) => {
          return (
            <div className="flex items-center gap-1.5">
              {row.report ? (
                <Popover
                  content={
                    <div className="max-w-xs custom-font text-xs p-1" style={{ fontFamily: 'CustomFont' }}>
                      <div className="font-bold mb-1 text-gray-700 dark:text-gray-200">گزارش ثبت‌شده:</div>
                      <div className="whitespace-pre-wrap text-gray-600 dark:text-gray-300 mb-2 leading-relaxed bg-amber-50 dark:bg-amber-950/40 p-2 rounded border border-amber-200 dark:border-amber-800">
                        {row.report}
                      </div>
                      <Button
                        size="small"
                        type="link"
                        className="p-0 custom-font text-xs text-teal-600"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenReportModal(row)}
                      >
                        ویرایش گزارش
                      </Button>
                    </div>
                  }
                  trigger="hover"
                >
                  <button
                    type="button"
                    onClick={() => handleOpenReportModal(row)}
                    className="text-right truncate max-w-[150px] text-xs text-amber-900 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded-md custom-font hover:border-amber-400 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <FileTextOutlined className="text-amber-600 dark:text-amber-400 shrink-0" />
                    <span className="truncate">{row.report}</span>
                  </button>
                </Popover>
              ) : (
                <Button
                  size="small"
                  type="dashed"
                  className="custom-font text-xs text-gray-400 hover:text-teal-600 border-dashed"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenReportModal(row)}
                >
                  ثبت گزارش
                </Button>
              )}
            </div>
          );
        },
      },
      {
        title: 'خلاصه هوش مصنوعی',
        key: 'summary',
        width: 240,
        render: (_, row) => {
          if (isAiInProgress(row.ai_status)) {
            return (
              <span className="custom-font text-xs text-purple-500 flex items-center gap-1.5">
                <LoadingOutlined /> در حال خلاصه‌سازی...
              </span>
            );
          }
          if (row.summary) {
            return (
              <Tooltip title={<span className="custom-font leading-6">{row.summary}</span>} overlayStyle={{ maxWidth: 420 }}>
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => openTextModal(row)}
                    className="custom-font text-right text-xs text-gray-700 dark:text-gray-200 line-clamp-2 leading-5 max-w-[230px] cursor-pointer bg-transparent border-0 p-0 hover:text-purple-600"
                  >
                    {row.summary}
                  </button>
                  <AiAnalysisTags analysis={row.ai_analysis} />
                </div>
              </Tooltip>
            );
          }
          if (row.ai_status === 'failed') {
            return (
              <Tooltip title={row.ai_error || 'خطا در خلاصه‌سازی'}>
                <Tag color="error" className="custom-font m-0 text-xs">خلاصه‌سازی ناموفق</Tag>
              </Tooltip>
            );
          }
          return <span className="text-gray-400 custom-font">—</span>;
        },
      },
      {
        title: 'عملیات',
        key: 'actions',
        width: 140,
        render: (_, row) => {
          const hasAudio = Boolean(row?.audio_file || row?.audio_url || row?.has_audio);
          const tStatus = row?.transcribe_status || (row?.is_transcribed ? 'completed' : 'none');
          const minDuration = Number(row?.transcribe_min_duration) || 30;
          const callDuration = Number(row?.duration) || 0;
          const isEligibleDuration = row?.can_transcribe !== undefined
            ? Boolean(row.can_transcribe)
            : (callDuration >= minDuration);
          const canTranscribe = hasAudio && (tStatus === 'none' || tStatus === 'failed') && isEligibleDuration;

          return (
            <Space size={0}>
              {tStatus === 'completed' && (
                <Tooltip title="رونویسی شده">
                  <span className="text-green-500 flex items-center justify-center px-2">
                    <FileDoneOutlined />
                  </span>
                </Tooltip>
              )}
              {tStatus === 'pending' && (
                <Tooltip title="در صف رونویسی...">
                  <span className="text-gray-400 flex items-center justify-center px-2">
                    <ClockCircleOutlined />
                  </span>
                </Tooltip>
              )}
              {tStatus === 'processing' && (
                <Tooltip title="در حال رونویسی... (ممکن است چند دقیقه زمان ببرد)">
                  <span className="text-purple-500 flex items-center justify-center px-2">
                    <LoadingOutlined />
                  </span>
                </Tooltip>
              )}
              {tStatus === 'failed' && (
                <Tooltip title={row.transcribe_error || 'خطا در رونویسی'}>
                  <span className="text-red-500 flex items-center justify-center px-2">
                    <CloseCircleOutlined />
                  </span>
                </Tooltip>
              )}
              {canTranscribe && (
                <Tooltip title="ارسال برای رونویسی">
                  <Button
                    type="link"
                    size="small"
                    icon={<AudioOutlined />}
                    loading={transcribingIds.includes(row.id)}
                    onClick={() => handleTranscribe(row.id)}
                    className="custom-font text-purple-500 hover:text-purple-600"
                  />
                </Tooltip>
              )}
              {canSummarizeCall(row) && !isAiInProgress(row.ai_status) && (
                <Tooltip title={row.summary ? 'تحلیل مجدد با هوش مصنوعی' : 'خلاصه‌سازی با هوش مصنوعی'}>
                  <Button
                    type="link"
                    size="small"
                    icon={<RobotOutlined />}
                    loading={summarizingIds.includes(row.id)}
                    onClick={() => handleSummarizeCall(row.id)}
                    className="custom-font text-purple-600 hover:text-purple-700"
                  />
                </Tooltip>
              )}
              {hasAudio && (
                <Tooltip title="پخش صدا">
                  <Button
                    type="link"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    loading={audioLoadingId === row.id}
                    onClick={() => playCallAudio(row)}
                    className="custom-font"
                  />
                </Tooltip>
              )}
              <Tooltip title="مشاهده متن و خلاصه تماس">
                <Button
                  type="link"
                  size="small"
                  icon={<FileTextOutlined />}
                  onClick={() => openTextModal(row)}
                  className="custom-font"
                />
              </Tooltip>
              <Tooltip title="مشاهده جزئیات">
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigateToDetail(`/call-center/calls/${row.id}`)}
                  className="custom-font"
                />
              </Tooltip>
              <Tooltip title={row.report ? 'ویرایش گزارش اپراتور' : 'ثبت گزارش اپراتور'}>
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenReportModal(row)}
                  className={`custom-font ${row.report ? 'text-amber-600 hover:text-amber-700' : 'text-gray-500 hover:text-teal-600'}`}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigateToDetail, audioLoadingId, handleTranscribe, transcribingIds, outcomes, loadingOutcomeId, handleUpdateOutcome, handleOpenReportModal, summarizingIds, handleSummarizeCall, savingSubjectId, handleUpdateSubject],
  );

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                <PhoneOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  تماس‌ها
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  تاریخچه مکالمات ورودی و خروجی
                </p>
              </div>
            </div>
            <Space wrap>
              {selectedRowKeys.length > 0 && (
                <Button
                  icon={<RobotOutlined />}
                  loading={bulkSummarizing}
                  onClick={handleSummarizeSelected}
                  className="custom-font text-purple-600 border-purple-400 hover:text-purple-700"
                >
                  خلاصه‌سازی {selectedRowKeys.length.toLocaleString('fa-IR')} تماس انتخاب‌شده
                </Button>
              )}
              <Button
                icon={<RobotOutlined />}
                onClick={() => setBulkModalOpen(true)}
                className="custom-font"
              >
                خلاصه‌سازی گروهی
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchCalls(pagination.current_page, pagination.per_page)}
                loading={loading}
              >
                بروزرسانی
              </Button>
              <PermissionGate permission="manage_callcenter_setting">
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={syncing}
                  onClick={openSyncModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  همگام‌سازی CDR
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <div className="mb-6">
              <StandardFilterPanel
                quickFilters={quickFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                totalCount={pagination.total}
                showTotalCount
                hideSearch
                compactFilterLayout
              />
            </div>
            {loading && calls.length === 0 ? (
              <TableSkeleton rows={8} columns={8} />
            ) : (
              <div className="w-full max-w-full overflow-x-auto">
                <Table
                  rowKey="id"
                  rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    preserveSelectedRowKeys: true,
                    getCheckboxProps: (row) => ({
                      disabled: !canSummarizeCall(row) || isAiInProgress(row.ai_status),
                      title: canSummarizeCall(row) ? undefined : 'فقط تماس‌های رونویسی‌شده قابل خلاصه‌سازی هستند',
                    }),
                  }}
                  columns={columns}
                  dataSource={calls}
                  loading={loading}
                  className="custom-font"
                  style={{ minWidth: 900 }}
                  pagination={{
                    current: pagination.current_page,
                    pageSize: pagination.per_page,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => (
                      <span className="custom-font">
                        نمایش {range[0]} تا {range[1]} از {total} تماس
                      </span>
                    ),
                    onChange: (page, pageSize) => handlePageChange(page, pageSize),
                    onShowSizeChange: (current, size) => handlePageChange(1, size),
                  }}
                  onChange={(tablePagination) => {
                    if (
                      tablePagination &&
                      (tablePagination.current !== pagination.current_page ||
                        tablePagination.pageSize !== pagination.per_page)
                    ) {
                      handlePageChange(tablePagination.current, tablePagination.pageSize);
                    }
                  }}
                  locale={{ emptyText: <span className="custom-font">تماسی یافت نشد. برای دریافت تاریخچه، همگام‌سازی CDR را اجرا کنید.</span> }}
                />
              </div>
            )}
          </div>
        </Card>

        <Modal
          title="همگام‌سازی سوابق تماس‌ها (CDR)"
          open={syncModalOpen}
          onCancel={() => setSyncModalOpen(false)}
          onOk={submitSync}
          confirmLoading={syncing}
          destroyOnClose
          okText="شروع همگام‌سازی"
          cancelText="انصراف"
          width={520}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingExtensions}>
            <Form form={syncForm} layout="vertical" className="custom-font" style={{ marginTop: 8 }}>
              <Form.Item
                name="from_date"
                label="از تاریخ"
                rules={[{ required: true, message: 'تاریخ شروع را انتخاب کنید' }]}
              >
                <PersianDatePicker placeholder="انتخاب تاریخ شروع" />
              </Form.Item>

              <Form.Item
                name="to_date"
                label="تا تاریخ"
                rules={[{ required: true, message: 'تاریخ پایان را انتخاب کنید' }]}
              >
                <PersianDatePicker placeholder="انتخاب تاریخ پایان" />
              </Form.Item>

              <Form.Item
                name="extensions"
                label="داخلی‌ها"
                rules={[{ required: true, message: 'حداقل یک داخلی انتخاب کنید' }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="انتخاب داخلی‌ها"
                  popupClassName="custom-font"
                  className="custom-font"
                  options={extensionOptions}
                  notFoundContent={
                    <span className="custom-font text-gray-500">داخلی یافت نشد</span>
                  }
                />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title={
            <span className="custom-font flex items-center gap-2">
              <FileTextOutlined />
              متن تماس
              {textModalCall?.phone ? (
                <span className="text-gray-400 text-sm font-normal" dir="ltr">
                  ({textModalCall.phone})
                </span>
              ) : null}
              {textModalCall?.subject && <CallSubjectChip subject={textModalCall.subject} size="small" />}
            </span>
          }
          open={textModalOpen}
          onCancel={() => {
            setTextModalOpen(false);
            setTextModalCall(null);
          }}
          footer={
            <Button onClick={() => setTextModalOpen(false)} className="custom-font">
              بستن
            </Button>
          }
          width={hasAnalysisReport(textModalCall) ? 880 : 720}
          destroyOnClose
        >
          <Spin spinning={textModalLoading}>
            <div className="custom-font space-y-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <CallAiSummaryPanel
                call={textModalCall}
                variant="compact"
                summarizing={textModalCall ? summarizingIds.includes(textModalCall.id) : false}
                onSummarize={handleSummarizeCall}
                onOpenFullReport={textModalCall ? () => {
                  setTextModalOpen(false);
                  navigateToDetail(`/call-center/calls/${textModalCall.id}`);
                } : undefined}
              />

              {textModalCall && (
                <CallTranscriptView
                  collapsible
                  segments={textModalCall.transcribed_segments}
                  transcript={textModalCall.transcript}
                />
              )}
            </div>
          </Spin>
        </Modal>

        <Modal
          title={
            <span className="custom-font flex items-center gap-2">
              <RobotOutlined className="text-purple-600" />
              خلاصه‌سازی گروهی تماس‌های فیلترشده
            </span>
          }
          open={bulkModalOpen}
          onCancel={() => setBulkModalOpen(false)}
          footer={null}
          width={760}
          destroyOnClose
        >
          <p className="custom-font text-xs text-gray-500 mb-4">
            فیلترهای فعلی لیست (نوع تماس، بازه تاریخ، داخلی و اپراتور) روی این عملیات اعمال می‌شوند. فقط تماس‌هایی که با موفقیت رونویسی شده‌اند ارسال خواهند شد.
          </p>
          <AiBulkSummaryCard
            baseFilters={bulkBaseFilters}
            showDateRange={false}
            onQueued={() => {
              setBulkModalOpen(false);
              fetchCalls(pagination.current_page, pagination.per_page);
            }}
          />
        </Modal>

        <Modal
          title={
            <span className="custom-font">
              پخش تماس
              {audioCallLabel ? (
                <span className="text-gray-400 text-sm font-normal mr-2" dir="ltr">
                  ({audioCallLabel})
                </span>
              ) : null}
            </span>
          }
          open={audioModalOpen}
          onCancel={() => {
            setAudioModalOpen(false);
            setAudioUrl(null);
          }}
          footer={
            <Button
              onClick={() => {
                setAudioModalOpen(false);
                setAudioUrl(null);
              }}
              className="custom-font"
            >
              بستن
            </Button>
          }
          width={520}
          destroyOnClose
        >
          {audioModalLoading ? (
            <div className="text-center py-8">
              <Spin tip="در حال دریافت لینک صوتی..." />
            </div>
          ) : audioUrl ? (
            <audio
              key={audioUrl}
              src={audioUrl}
              controls
              autoPlay
              style={{ width: '100%' }}
              className="custom-font"
            >
              مرورگر شما از پخش صوت پشتیبانی نمی‌کند.
            </audio>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="فایل صوتی در دسترس نیست" />
          )}
        </Modal>

        {/* مودال ویرایش/ثبت گزارش تماس */}
        <CallReportModal
          call={editingCallForReport}
          open={reportModalOpen}
          onClose={handleCloseReportModal}
          onSaved={handleReportSaved}
        />
      </div>
    </MainLayout>
  );
};

export default CallCenterCalls;
